import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  EmailTemplateTypes,
  PolicyDayPeriodType,
  PolicyTargetStatus,
} from 'shared';

import { Grid } from '../../../../../common/Atoms/Grid';
import { FormRow } from '../../../Molecules/Form/FormRow';
import { GridItem } from '../../../../../common/Atoms/GridItem';
import { Select } from '../../../../../common/Atoms/Select';
import { fieldIsValid } from '../../../../lib/helper';
import { TextEditor } from '../../../Molecules/TextEditor/TextEditor';
import { useMergeTags } from '../../../../hooks/useMergeTagsAndLinks';
import { FormLabel } from '../../../../../common/Atoms/FormLabel';
import { FormDescription } from '../../../../../common/Atoms/FormDescription';
import { IPolicyProps } from '../PolicyItem';
import { DayIndexWithType } from '../PolicyForm/Fields/DayIndexWithType';
import { SkipWhenPromisedOrQueryOpen } from '../PolicyForm/Fields/SkipWhenPromisedOrQueryOpen';

function calculateDayTime(value: number): `before` | `after` | `on` {
  if (value === 0) {
    return `on`;
  }

  if (value < 0) {
    return `before`;
  }

  return `after`;
}

export function PolicySms(props: IPolicyProps) {
  const { policy, invalids, handleChange } = props;
  const [dayType, setDayType] = useState(calculateDayTime(policy.day_index));
  const mergeTags = useMergeTags(EmailTemplateTypes.SMS); // TODO: Other types

  useEffect(() => {
    setDayType(calculateDayTime(policy.day_index));
  }, [ policy.day_index ]);

  const formattedMergeTags = useMemo(() => {
    return mergeTags.asArray
      .filter(tag => tag.value !== `ForecastedPenaltyAmount` && !tag.archived)
      .map(tag => {
        return {
          text: tag.name,
          value: tag.value,
        };
      });
  }, [ mergeTags ]);

  const dayTypes = useMemo(() => {
    const result =[
      {
        label: `After`,
        value: `after`,
      },
      {
        label: `On The`,
        value: `on`,
      },
    ];

    if (policy.target_status === PolicyTargetStatus.DUE || policy.target_status === PolicyTargetStatus.PROMISE_DUE) {
      result.unshift({
        label: `Before`,
        value: `before`,
      });
    }

    // The promise closes a day or so after the promised date, so "After" is covered by Promise Broken
    if (policy.target_status === PolicyTargetStatus.PROMISE_DUE) {
      return result.filter(o => o.value !== `after`);
    }

    return result;
  }, [ policy.target_status ]);

  const invoiceStates = [
    {
      label: `Due Date`,
      value: PolicyTargetStatus.DUE,
    },
    {
      label: `Issue Date`,
      value: PolicyTargetStatus.ISSUED,
    },
    {
      label: `Date Marked Paid`,
      value: PolicyTargetStatus.MARKED_PAID,
    },
    {
      label: `Promise Broken`,
      value: PolicyTargetStatus.PROMISE_BROKEN,
      tooltip: `The date a payment promise was broken. Fires again for each broken promise.`,
    },
    {
      label: `Promised Payment Date`,
      value: PolicyTargetStatus.PROMISE_DUE,
      tooltip: `The date the customer promised to pay. Never skipped by "skip when payment promised".`,
    },
  ];

  const dayIndexValue = useMemo(() => {
    let str = policy.day_index.toString();

    if (str.startsWith(`-`)) {
      str = str.replace(`-`, ``);
    }

    return str;
  }, [ policy.day_index ]);

  function onDayIndexChange(e, day = dayType) {
    const { value } = e;

    let num = Number(value);

    if (Number.isNaN(num)) {
      handleChange({
        name: `day_index`,
        value,
      });

      return;
    }

    if (num !== 0) {
      if (day === `before`) {
        num = num < 0 ? num : -num;
      }

      if (day === `after`) {
        num = num > 0 ? num : -num;
      }

      if (day === `on`) {
        num = 0;
      }
    }

    handleChange({
      name: `day_index`,
      value: num,
    });
  }

  function handleDayTypeChange(e) {
    setDayType(e.value);

    onDayIndexChange({
      value: policy.day_index,
    }, e.value);

    if (e.value === `on` && policy.day_period_type === PolicyDayPeriodType.WEEKDAYS) {
      // Set weekdays to days if they select `on`
      handleChange({
        name: `day_period_type`,
        value: PolicyDayPeriodType.DAYS,
      });
    }
  }

  function handleSmsContentChange({ text }) {
    handleChange({
      name: `sms_content`,
      value: text,
    });
  }

  return (
    <>
      { /* RULE */ }
      <Grid cols={ 3 }
        className={ `items-end` }>
        <GridItem>
          <DayIndexWithType
            handleChange={ handleChange }
            policyFormData={ policy }
            invalids={ invalids }
            dayType={ dayType }
            dayIndexValue={ dayIndexValue }
            onDayIndexChange={ onDayIndexChange }
            allowOrLater={ true }
            label={ `Send this notification:` }
          />
        </GridItem>
        <GridItem>
          <Select
            options={ dayTypes }
            selected={ dayType }
            onChange={ handleDayTypeChange }
          />
        </GridItem>
        <GridItem>
          <Select
            options={ invoiceStates }
            selected={ policy.target_status }
            onChange={ handleChange }
            name={ `target_status` }
            invalid={ fieldIsValid(`target_status`, invalids) }
          />
        </GridItem>
      </Grid>
      <FormRow className={ `space-y-1` }>
        <FormLabel>{ `Text Message Content` }</FormLabel>
        <FormDescription>{ `Use merge tags to dynamic insert values. Maximum 320 characters` }</FormDescription>
        <FormDescription>{ `Will be sent to the primary mobile phone number on file for the invoice contact if available.` }</FormDescription>
        <TextEditor
          value={ policy.sms_content }
          onChange={ handleSmsContentChange }
          mergeTags={ formattedMergeTags }
          mode={ `sms` }
        />
      </FormRow>
      <FormRow>
        <SkipWhenPromisedOrQueryOpen
          handleChange={ handleChange }
          policyFormData={ policy }
          invalids={ invalids }
        />
      </FormRow>
    </>
  );
}
